import { useMemo } from 'react';
import { CircleDot, Puzzle } from 'lucide-react';
import { FilterBar } from './FilterBar';
import { FILTER_ALL_VALUE, type FilterDefinition, type FilterOption } from './filter-types';
import type { PluginFilters, PluginStatusFilter, PluginTypeFilter } from './plugin-filter-utils';

const ALL = FILTER_ALL_VALUE;

const STATUS_OPTIONS: FilterOption[] = [
  { value: 'active', label: 'Active' },
  { value: 'disabled', label: 'Disabled' },
];

const TYPE_OPTIONS: FilterOption[] = [
  { value: 'server', label: 'Server' },
  { value: 'web', label: 'Web' },
  { value: 'auth', label: 'Auth' },
  { value: 'webhook-provider', label: 'Webhook Provider' },
  { value: 'webhook-receiver', label: 'Webhook Receiver' },
];

interface PluginFilterBarProps {
  filters: PluginFilters;
  onStatusChange: (value: PluginStatusFilter) => void;
  onTypeChange: (value: PluginTypeFilter) => void;
  className?: string;
}

export function PluginFilterBar({
  filters,
  onStatusChange,
  onTypeChange,
  className,
}: PluginFilterBarProps) {
  const filterDefinitions = useMemo<FilterDefinition[]>(
    () => [
      {
        id: 'status',
        label: 'Status',
        icon: CircleDot,
        value: filters.status ?? ALL,
        options: STATUS_OPTIONS,
        onValueChange: (v) => onStatusChange(v === ALL ? null : (v as PluginStatusFilter)),
        searchPlaceholder: 'Search statuses…',
      },
      {
        id: 'type',
        label: 'Type',
        icon: Puzzle,
        value: filters.type ?? ALL,
        options: TYPE_OPTIONS,
        onValueChange: (v) => onTypeChange(v === ALL ? null : (v as PluginTypeFilter)),
        searchPlaceholder: 'Search types…',
      },
    ],
    [filters.status, filters.type, onStatusChange, onTypeChange],
  );

  const clearFilters = () => {
    onStatusChange(null);
    onTypeChange(null);
  };

  return <FilterBar filters={filterDefinitions} onClear={clearFilters} className={className} />;
}
